import { useUser } from "@features/user"
import useEnv from "./useEnv"

interface MenuLeaf {
  labelKey: string
  icon: string
  route: string
  external: boolean
}

interface MenuItem extends MenuLeaf {
  visible: boolean
  children?: MenuLeaf[]
}

const useLeftMenu = (): MenuItem[] => {
  const { data } = useUser()
  const { geconturUrl } = useEnv()

  if (!data) {
    return []
  }

  const { can } = data

  const adminChildren: (MenuLeaf & { visible?: boolean })[] = [
    { labelKey: "adminUsers", icon: "fa fa-shield", route: "/admin/users", external: false, visible: can.manageUsers },
    { labelKey: "adminRoles", icon: "fa fa-tags", route: "/admin/roles", external: false, visible: can.manageRoles },
    { labelKey: "adminPermissions", icon: "fa fa-key", route: "/admin/permissions", external: false, visible: can.managePermissions },
    { labelKey: "agencies", icon: "fa fa-building-o", route: "/agencies", external: false, visible: can.manageAgencies },
    { labelKey: "tags", icon: "fa fa-tags", route: "/tags", external: false, visible: can.viewMenuAdmin },
    { labelKey: "dynamicDropdowns", icon: "fa fa-list", route: "/dynamic-dropdowns", external: false, visible: can.viewMenuAdmin },
    { labelKey: "notificationAssignments", icon: "fa fa-bell", route: "/notification-assignments", external: false, visible: can.manageNotificationAssignments },
    { labelKey: "documentCategories", icon: "fa fa-files-o", route: "/document-categories", external: false, visible: can.viewMenuDocumentCategories },
  ].filter((c) => c.visible !== false)

  return [
    {
      labelKey: "home",
      icon: "fa fa-dashboard",
      route: "/",
      external: false,
      visible: true,
    },
    {
      labelKey: "agencyUsers",
      icon: "fa fa-users",
      route: "/agency-users",
      external: false,
      visible: can.manageAgencyUsers,
    },
    {
      labelKey: "agencyVisits",
      icon: "fa fa-handshake-o",
      route: "/agency-visits",
      external: false,
      visible: can.viewMenuAgencyVisits || can.viewMenuAgencyAllVisits,
    },
    {
      labelKey: "travelChecks",
      icon: "fa fa-money",
      route: "/travelers-check",
      external: false,
      visible: can.viewMenuTravelerChecks,
    },
    {
      labelKey: "contacts",
      icon: "fa fa-address-book-o",
      route: "/contacts",
      external: false,
      visible: can.viewMenuContacts,
    },
    {
      labelKey: "documents",
      icon: "fa fa-files-o",
      route: "/documents",
      external: false,
      visible: can.viewMenuDocuments,
    },
    {
      labelKey: "messagePopup",
      icon: "fa fa-window-restore",
      route: `${geconturUrl}/message`,
      external: true,
      visible: can.manageMessages,
    },
    {
      labelKey: "inquiries",
      icon: "fa fa-bar-chart",
      route: `${geconturUrl}/inquiry`,
      external: true,
      visible: can.manageInquiries,
    },
    {
      labelKey: "offers",
      icon: "fa fa-cubes",
      route: "/offers",
      visible: can.viewMenuPdf,
      external: false, // You can assume internal navigation here, or adjust as needed
    },
    {
      labelKey: "newsletters",
      icon: "fa fa-envelope",
      route: "/newsletter",
      visible: can.viewMenuNewslettersNew,
      external: false,
    },
    {
      labelKey: "signature",
      icon: "fa fa-credit-card",
      route: "/signature",
      visible: can.viewMenuSignature,
      external: false,
    },
    {
      labelKey: "homeLayout",
      icon: "fa fa-columns",
      route: `${geconturUrl}supplier`,
      external: true,
      visible: can.viewMenuSuppliers,
    },
    {
      labelKey: "marketing",
      icon: "fa fa-picture-o",
      route: `${geconturUrl}newsletter-headers`,
      external: true,
      visible: can.viewMenuMarketing,
    },
    {
      labelKey: "intranet",
      icon: "fa fa-fa fa-browser",
      route: "https://intranet.newtour.pt/site/login",
      external: true,
      visible: can.menuIntranetNewtour,
    },
    {
      labelKey: "adminTools",
      icon: "fa fa-gears",
      route: `${geconturUrl}/users`,
      external: true,
      visible: can.viewMenuAdmin,
    },
    {
      labelKey: "admin",
      icon: "fa fa-gear",
      route: "",
      external: false,
      visible: can.manageUsers || can.manageRoles || can.managePermissions || can.viewMenuAdmin || can.manageNotificationAssignments || can.viewMenuDocumentCategories,
      children: adminChildren,
    },
  ]
    .filter(({ visible }) => visible)
    .map((item) => ({
      ...item,
      route: item.route || "",
    }))
}

export default useLeftMenu
